import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import {
	ICharacterInitialClientData,
	IPhoneNotification,
	IPhoneNotificationsCount,
	IPhoneSettings,
	PhoneApps,
} from '~/shared';
import { BackendService } from '../BackendService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService';
import { IPhoneService } from './phoneServiceTypes';

export class PhoneService implements IPhoneService {
	private readonly DEFAULT_PHONE_SETTINGS: IPhoneSettings = {
		brightness: 1,
		wallpaperIndex: 0,
		notificationsEnabled: true,
		soundsEnabled: true,
	};
	private readonly MAX_WALLPAPER_INDEX = 5;

	private activeApp = new Subject<PhoneApps | null>(null);
	private phoneNumber = new Subject<string>('');
	private settings = new Subject<IPhoneSettings>({
		...this.DEFAULT_PHONE_SETTINGS,
	});
	private notificationState = new Subject<IPhoneNotification | null>(null);
	private notificationsCount = new Subject<IPhoneNotificationsCount[]>([]);

	constructor(
		private readonly rpc: RpcService,
		private readonly vars: VarsService,
		private readonly backend: BackendService,
	) {
		this.rpc.register('phone:setActiveApp', (app: PhoneApps | null) => {
			this.activeApp.next(app);
		});

		this.backend.addEventHandler(
			'character:init',
			this.handleCharacterInit.bind(this),
		);

		this.vars.get('phone:phoneNumber', '').subscribe((value: string) => {
			this.phoneNumber.next(value);
		});

		this.vars
			.get('phone:notificationState', null)
			.subscribe((state: IPhoneNotification | null) => {
				this.notificationState.next(state);
			});

		this.vars
			.get('phone:notificationsCount', [])
			.subscribe((value: IPhoneNotificationsCount[]) => {
				this.notificationsCount.next(value);
			});
	}

	private handleCharacterInit(payload: ICharacterInitialClientData) {
		const settings = {
			...this.DEFAULT_PHONE_SETTINGS,
			...payload.phoneSettings,
		};

		settings.wallpaperIndex = Math.min(
			Math.max(settings.wallpaperIndex, 0),
			this.MAX_WALLPAPER_INDEX,
		);
		settings.brightness = Math.min(Math.max(settings.brightness, 0), 1);

		this.settings.next(settings);
	}

	public getActiveApp(): IObservable<PhoneApps | null> {
		return this.activeApp;
	}

	@notifyOnError
	public async openCamera(): Promise<void> {
		await this.rpc.invokeClient('phone:openApp:camera');
	}

	@notifyOnError
	public async openGps(): Promise<void> {
		await this.rpc.invokeClient('phone:openApp:gps');
	}

	@notifyOnError
	public async closeGps(): Promise<void> {
		await this.rpc.invokeClient('phone:closeApp:gps');
	}

	@notifyOnError
	public async openAds(): Promise<void> {
		await this.rpc.invokeClient('phone:openApp:ads');
	}

	@notifyOnError
	public async closeAds(): Promise<void> {
		await this.rpc.invokeClient('phone:closeApp:ads');
	}

	public getSettings(): IObservable<IPhoneSettings> {
		return this.settings;
	}

	public setBrightnessValue(newValue: number): void {
		const clampedValue = Math.min(Math.max(newValue, 0), 1);

		this.updateSettings({ brightness: clampedValue });
	}

	public async setActiveWallpaperIndex(newValue: number): Promise<void> {
		const clampedValue = Math.min(
			Math.max(newValue, 0),
			this.MAX_WALLPAPER_INDEX,
		);

		await this.updateSettings({ wallpaperIndex: clampedValue });
	}

	public async setNotificationsEnabled(isEnabled: boolean): Promise<void> {
		await this.updateSettings({ notificationsEnabled: isEnabled });
	}

	public async setSoundsEnabled(isEnabled: boolean): Promise<void> {
		await this.updateSettings({ soundsEnabled: isEnabled });
	}

	@notifyOnError
	private async updateSettings(
		newSettings: Partial<IPhoneSettings>,
	): Promise<void> {
		await this.backend.invoke('characters:updatePhoneSettings', newSettings);

		const prevSettings = this.getSettings().getValue();
		const nextSettings = { ...prevSettings, ...newSettings };

		this.settings.next(nextSettings);
	}

	public getNotificationState(): IObservable<IPhoneNotification | null> {
		return this.notificationState;
	}

	public getPhoneNumber(): IObservable<string> {
		return this.phoneNumber;
	}

	public getNotificationsCount(): IObservable<IPhoneNotificationsCount[]> {
		return this.notificationsCount;
	}

	public async exit(): Promise<void> {
		return await this.rpc.invokeClient('phone:exit');
	}
}
